import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SwalService } from '../../service/swal.service';
import { ConnectionService } from '../../service/connection.service';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';

const TOUR_KEYS = ['tour_home', 'tour_eventi', 'tour_evento', 'tour_profilo'];

export interface EmailNotifications {
  contract_signed: boolean;
  quote_accepted: boolean;
  event_reminder: boolean;
  collaborator_invite: boolean;
  collaborator_response: boolean;
  deposit_reminder: boolean;
  contract_expiry: boolean;
  weekly_recap: boolean;
  app_news: boolean;
}

const DEFAULTS: EmailNotifications = {
  contract_signed: true,
  quote_accepted: true,
  event_reminder: true,
  collaborator_invite: true,
  collaborator_response: true,
  deposit_reminder: true,
  contract_expiry: true,
  weekly_recap: true,
  app_news: false,
};

@Component({
  selector: 'app-impostazioni',
  imports: [CommonModule, FormsModule, TranslatePipe, IconComponent, LoadErrorComponent],
  templateUrl: './impostazioni.component.html',
  styleUrl: './impostazioni.component.css',
})
export class ImpostazioniComponent implements OnInit {
  private translate = inject(TranslateService);
  private swal = inject(SwalService);
  private router = inject(Router);
  private connection = inject(ConnectionService);

  currentLang = this.translate.getCurrentLang() || 'it';
  sendingTest: string | null = null;

  notifications: EmailNotifications = { ...DEFAULTS };

  quoteValidityDays = 30;

  readonly notificationItems = [
    'contract_signed',
    'quote_accepted',
    'event_reminder',
    'collaborator_invite',
    'collaborator_response',
    'deposit_reminder',
    'contract_expiry',
    'weekly_recap',
    'app_news',
  ];

  // Su smartphone ogni sezione è una riga che si apre (una alla volta)
  openSection: string | null = null;

  toggleSection(key: string): void {
    this.openSection = this.openSection === key ? null : key;
  }

  get currentLangLabel(): string {
    return this.langs.find((l) => l.code === this.currentLang)?.label ?? '';
  }

  readonly langs = [
    { code: 'it', label: 'IT' },
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
  ];

  readonly tours: { key: string; label: string; route: string | null }[] = [
    { key: 'tour_home',    label: 'settings.tour.labels.home',    route: '/home' },
    { key: 'tour_eventi',  label: 'settings.tour.labels.events',  route: '/eventi' },
    { key: 'tour_evento',  label: 'settings.tour.labels.event',   route: null },
    { key: 'tour_profilo', label: 'settings.tour.labels.profile', route: '/profilo' },
  ];

  // ── Visibilità sezioni per Collaboratori / Team ──
  readonly visibilitySections: { key: string; label: string }[] = [
    { key: 'event_data', label: 'profile.visibility.sections.event_data' },
    { key: 'trial', label: 'profile.visibility.sections.trial' },
    { key: 'photos', label: 'profile.visibility.sections.photos' },
    { key: 'client', label: 'profile.visibility.sections.client' },
    { key: 'services', label: 'profile.visibility.sections.services' },
    { key: 'participants', label: 'profile.visibility.sections.participants' },
    { key: 'contract', label: 'profile.visibility.sections.contract' },
    { key: 'documents', label: 'profile.visibility.sections.documents' },
  ];

  private readonly defaultCollaboratorVisibility: Record<string, boolean> = {
    event_data: true, trial: true, photos: true, client: false, services: false, participants: true, contract: false, documents: false,
  };
  private readonly defaultTeamVisibility: Record<string, boolean> = {
    event_data: true, trial: true, photos: true, client: false, services: true, participants: true, contract: false, documents: false,
  };

  collaboratorVisibility: Record<string, boolean> = {};
  teamVisibility: Record<string, boolean> = {};

  // Preferenze non arrivate: notifiche, visibilità e validità restano nascoste,
  // altrimenti un tocco salverebbe i valori predefiniti al posto di quelli veri
  loadError = false;

  ngOnInit() {
    this.load();
  }

  load() {
    this.connection.get('@auth/profile', (data: any) => {
      if (data.email_notifications) {
        this.notifications = { ...DEFAULTS, ...data.email_notifications };
      }
      this.initVisibility(data);
      this.quoteValidityDays = +data.quote_validity_days || 30;
      this.loadError = false;
    }, () => (this.loadError = true));
  }

  saveQuoteValidity() {
    const days = Math.min(90, Math.max(1, Math.round(+this.quoteValidityDays) || 30));
    this.quoteValidityDays = days;
    this.connection.put(
      '@auth/preferences',
      { quote_validity_days: days },
      () => this.swal.success(this.translate.instant('quote.settings.saved')),
      () => {},
    );
  }

  private initVisibility(data: any) {
    const buildFrom = (saved: any, defaults: Record<string, boolean>): Record<string, boolean> => {
      const out: Record<string, boolean> = {};
      for (const s of this.visibilitySections) {
        out[s.key] = saved?.[s.key] ?? defaults[s.key];
      }
      return out;
    };
    this.collaboratorVisibility = buildFrom(data.collaborator_visibility, this.defaultCollaboratorVisibility);
    this.teamVisibility = buildFrom(data.team_visibility, this.defaultTeamVisibility);
  }

  toggleCollaboratorVisibility(key: string) {
    this.collaboratorVisibility[key] = !this.collaboratorVisibility[key];
  }

  toggleTeamVisibility(key: string) {
    this.teamVisibility[key] = !this.teamVisibility[key];
  }

  saveVisibility() {
    this.connection.put(
      'auth/visibility',
      {
        collaborator_visibility: this.collaboratorVisibility,
        team_visibility: this.teamVisibility,
      },
      () => this.swal.success(this.translate.instant('settings.visibility_saved')),
      () => this.swal.errorDialog(
        this.translate.instant('swal.error'),
        this.translate.instant('settings.visibility_error'),
      ),
    );
  }

  getNotif(key: string): boolean {
    return (this.notifications as any)[key] ?? false;
  }

  toggleNotif(key: keyof EmailNotifications) {
    this.notifications[key] = !this.notifications[key];
    this.saveNotifications();
  }

  private saveNotifications() {
    this.connection.put('@auth/preferences', { email_notifications: this.notifications }, () => {}, () => {});
  }

  sendTest(key: string) {
    this.sendingTest = key;
    this.connection.post(
      '@auth/notifications/test',
      { type: key },
      () => { this.sendingTest = null; this.swal.success(this.translate.instant('settings.notifications.test_sent')); },
      () => {
        this.sendingTest = null;
        this.swal.errorDialog(this.translate.instant('swal.error'), this.translate.instant('settings.notifications.test_error'));
      },
    );
  }

  changeLang(lang: string) {
    this.translate.use(lang as 'it' | 'en' | 'es');
    this.currentLang = lang;
    localStorage.setItem('lang', lang);
    this.connection.put('@auth/preferences', { preferred_lang: lang }, () => {}, () => {});
  }

  resetTour(t: { key: string; route: string | null }) {
    localStorage.removeItem(t.key);
    localStorage.setItem('tour_request', t.key);
    const done = TOUR_KEYS.filter(k => localStorage.getItem(k) === 'done');
    if (t.route) {
      this.connection.put('@auth/preferences', { tours_completed: done }, () => {}, () => {});
      this.router.navigate([t.route]);
    } else {
      // Il messaggio va mostrato nella callback della PUT: la risposta chiude
      // sempre lo Swal, quindi mostrarlo prima lo farebbe sparire dopo mezzo secondo.
      this.connection.put(
        '@auth/preferences',
        { tours_completed: done },
        () => this.swal.success(this.translate.instant('settings.tour.reset_event_msg')),
        () => {},
      );
    }
  }

  resetAllTours() {
    TOUR_KEYS.forEach(k => localStorage.removeItem(k));
    localStorage.setItem('tour_request', 'tour_home');
    this.connection.put(
      '@auth/preferences',
      { tours_completed: [] },
      () => this.router.navigate(['/home']),
      () => {},
    );
  }

  goToProfile() {
    this.router.navigate(['/profilo']);
  }

  openContractWizard() {
    localStorage.setItem('contract_wizard_pending', 'true');
    this.router.navigate(['/contratti']);
  }

  // Su mobile il bottone fluttuante di Crisp resta nascosto (vedi app.component.ts):
  // qui lo si mostra e apre esplicitamente, unico punto d'accesso da telefono.
  openSupportChat(): void {
    const crisp = (window as any).$crisp;
    if (!crisp) return;
    crisp.push(['do', 'chat:show']);
    crisp.push(['do', 'chat:open']);
  }
}
